import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import enCommon from './locales/en/common.json';

/**
 * i18n is wired from day one with English as the only locale.
 *
 * This is deliberate cheap insurance: adding a language later is a translation
 * job, whereas retrofitting i18n into a few hundred hardcoded strings is a
 * refactor. Two rules keep that promise intact:
 *   1. No user-visible string is written inline in a component. Use `t()`.
 *   2. No physical CSS directions. Use logical utilities (`ms-*`, `pe-*`,
 *      `start-*`, `text-start`) so `dir="rtl"` just works when an RTL
 *      language is added — ThemeProvider sets `dir` on <html>.
 */
export const SUPPORTED_LANGUAGES = ['en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

void i18n.use(initReactI18next).init({
  resources: {
    en: { common: enCommon },
  },
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  defaultNS: 'common',
  interpolation: {
    // React already escapes everything it renders.
    escapeValue: false,
  },
});

/** RTL languages, for when Arabic is added. Drives the document direction. */
const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur']);

export function directionFor(language: string): 'ltr' | 'rtl' {
  return RTL_LANGUAGES.has(language.split('-')[0] ?? '') ? 'rtl' : 'ltr';
}

export default i18n;
